import React, {useEffect, useMemo, useState} from "react";
import { useAppDispatch, useAppSelector } from "../../utils/Hooks";
import { Link, useLocation, useParams } from "react-router-dom";
import { LoggedInView } from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import { BreadcrumbLink } from "../../models/BreadcrumbLink";
import {
    getCurrentClient,
    updateClient,
    resetUpdateClientStatus,
} from "../../store/reducers/ClientsSlice";
import { RequestStatus } from "../../utils/Enums";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import ElogCheckbox from "../common/form/ElogCheckbox";
import CategoryType from "../../models/CategoryType";
import {getCountries} from "../../store/reducers/CountriesSlice";
import {apiClient} from "../../utils/ApiClient";
import ElogTextarea from "../common/form/ElogTextarea";
import {FormikProvider, useFormik, Field, Form} from "formik";
import {updateVendor} from "../../store/reducers/VendorsSlice";

interface ModifyClientScreenProps {}

export interface ModifyClientFormProps {
    name: string;
    email: string;
    deadline?: string;
    address?: string;
    country: string;
    reg_code?: string;
    vat?: string;
    no_vat: boolean;
    type: string;
    smartbooks_id: string;
    contact_name: string;
    contact_plea: string;
    contact_email: string;
    contact_phone: string;
    groups: string[];
}

export const ModifyClientScreen: React.FC<ModifyClientScreenProps> = () => {

    const dispatch = useAppDispatch();
    const location = useLocation();

    const { id } = useParams();

    const clientsState = useAppSelector((state) => state.clients);
    const countriesState = useAppSelector(state => state.countries);

    const requestStatus: RequestStatus = clientsState.updateClientStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        { text: "Kliendid", url: "/clients" },
        { text: "Muuda klienti", url: `/clients/${id}/modify` },
    ];

    const [countries, setCountries] = useState<Option[]>([]);
    const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);

    useEffect(() => {
        dispatch(resetUpdateClientStatus());
    }, [location]);

    useEffect(() => {
        dispatch(getCurrentClient({ id }));
    }, [id]);

    useEffect(() => {

        const countryOptions: Option[] = [];

        countriesState.countries.map(country => {

            const option: Option = {
                value: String(country.id),
                label: country.name
            }

            countryOptions.push(option);

        });

        setCountries(countryOptions);

    }, [countriesState.countries]);

    useEffect(() => {

        dispatch(getCountries());

        apiClient.get("category-types/all-by-category").then((response) => {
            setCategoryTypes(response.data);
        });

    }, []);

    const typeOptions: Option[] = [
        {value: 'local', label: 'Ettevõte'},
        {value: 'person', label: 'Eraklient'},
        {value: 'foreign', label: 'Välisettevõte'},
        {value: 'foreignPerson', label: 'Välisriigi eraisik'},
    ];

    const selectedCategoryIds = useMemo(() => {
        return clientsState.currentClient?.categories?.map((category) => String(category.id));
    }, [clientsState.currentClient]);

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            name: clientsState?.currentClient?.name ?? "",
            email: clientsState?.currentClient?.email ?? "",
            deadline: clientsState?.currentClient?.deadline ?? "",
            address: clientsState?.currentClient?.address ?? "",
            city: clientsState?.currentClient?.city ?? "",
            postCode: clientsState?.currentClient?.post_code ?? "",
            smartbooksId: clientsState?.currentClient?.smartbooks_id ?? "",
            country: clientsState?.currentClient?.country_id ?? countries[0]?.value,
            regCode: clientsState?.currentClient?.reg_code ?? "",
            comment: clientsState?.currentClient?.comment ?? "",
            vat: clientsState?.currentClient?.vat ?? "",
            noVat: !!clientsState?.currentClient?.no_vat,
            type: (clientsState?.currentClient?.type && clientsState?.currentClient?.type?.length > 0) ? clientsState?.currentClient?.type : typeOptions[0].value,
            finance: clientsState?.currentClient?.finance === 1,
            prepayment: clientsState?.currentClient?.prepayment === 1,
            groups: selectedCategoryIds,
        },
        onSubmit: (values: any) => {
            dispatch(updateClient({ id, clientData: values }));
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    const getClientData = async () => {
        try {
            // @ts-ignore
            const response = await apiClient.get(`client-info/${values.regCode}`);
            const data = response.data.data;

            setFieldValue('name', data.name);
            setFieldValue('email', data.emails);

            if ( !!data.vatNo ) {
                setFieldValue('vat', data.vatNo);
            } else {
                setFieldValue('noVat', true);
            }

            setFieldValue('address', data.street);
            setFieldValue('postCode', data.postCode);
            setFieldValue('city', data.city);

        } catch (error) {
            //console.error("Error fetching client data:", error);
            // Handle error
        }
    };

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks} />
            <br /><Link to={`/clients/${id}/contacts`}>Kontaktid</Link><br />
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Kliendi andmed uuendatud</h2>
                        <span><Link to={`/clients/${id}/contacts`}>Muuda ka kontakte</Link></span>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && clientsState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {clientsState.errors.map((error, index) => (
                            <span key={index}>{error}<br /></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>Kliendi andmed</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Ettevõte" name="name" mandatory={true}/>
                            <ElogInput label="Arve E-maili aadress" name="email" mandatory={true}/>
                            <ElogInput label="Maksetähtaeg" name="deadline"/>
                            <ElogInput label="Aadress" name="address"/>
                            <ElogInput label="Linn" name="city"/>
                            <ElogInput label="Postiindeks" name="postCode"/>
                            <ElogSelect label="Riik" name="country" options={countries}/>
                            <ElogInput label="Registrikood" name="regCode" mandatory={true}/>
                            <tr>
                                <th></th>
                                <td><a href="Javascript:;" onClick={() => {getClientData()}}>Võta andmed</a></td>
                            </tr>
                            <ElogInput label="KM number" name="vat"/>
                            <ElogCheckbox label="Pole km kohustlane" name="noVat" />
                            <ElogSelect label="Tüüp" name="type" options={typeOptions}/>
                            <ElogTextarea label="Kommentaar" name="comment"/>
                            <ElogCheckbox label="Maksetähtajaga klient" name="finance" />
                            <ElogCheckbox label="Ettemaks" name="prepayment" />
                            <ElogInput label="Smarbooks ID" name="smartbooksId"/>
                            </tbody>
                        </table>
                    </div>

                    {categoryTypes.map((categoryType) => (
                        <div key={categoryType.id} className="form">
                            <h2>
                                <span>{categoryType.name}</span>
                            </h2>
                            {categoryType?.categories?.map((category) => (
                                <Field
                                    key={category.id}
                                    name="groups"
                                    type="checkbox"
                                    value={String(category.id)}
                                    render={(props: { field: { value: any; onChange: (e: React.ChangeEvent<any>) => void; onBlur: () => void; name: string } }) => (
                                        <>
                                            <label htmlFor={`group_${category.id}`}>{category.name}</label>
                                            <input
                                                {...props.field}
                                                id={`group_${category.id}`}
                                                type="checkbox"
                                                onChange={(event) => props.field.onChange(event)}
                                                onBlur={() => undefined}
                                            />
                                        </>
                                    )}
                                />
                            ))}
                        </div>
                    ))}

                    {requestStatus === RequestStatus.Success && (
                        <div className="message msg-ok">
                            <div>
                                <h2>Kliendi andmed uuendatud</h2>
                                <span><Link to={`/clients/${id}/contacts`}>Muuda ka kontakte</Link></span>
                            </div>
                        </div>
                    )}
                    {requestStatus === RequestStatus.Failed && clientsState.errors.length > 0 && (
                        <div className="message msg-error">
                            <div>
                                <h2>Viga:</h2>
                                {clientsState.errors.map((error, index) => (
                                    <span key={index}>{error}<br /></span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="action clear">
                        <p>
                            <button type="submit">
                                    <span>
                                        <strong>Muuda</strong>
                                    </span>
                            </button>
                            <Link to="/clients">Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};